import { MOSIDA_API_BASE_URL } from '../config/apiUrls';
import { fetchWithRetry } from '../utils/httpClient';
import { getStoredAuthUser } from '../utils/authSession';

const baseUrl = MOSIDA_API_BASE_URL;

/** Logged-in kiosk user id for payment QR / save-card association. */
export function resolvePaymentUserId(explicit?: string | null): string | null {
  const fromExplicit = explicit != null ? String(explicit).trim() : '';
  if (fromExplicit) return fromExplicit;
  return getStoredAuthUser()?.id ?? null;
}

export interface PaymentQRResponse {
  success: boolean;
  data?: string | {
    qrCodeUrl?: string;
    qrCode?: string;
    paymentLink?: string;
    sessionId?: string;
    [key: string]: any;
  };
  message?: string;
  error?: string;
}

export interface GeneratePaymentLinkPayload {
  data: {
    items: Array<{
      id: string | number;
      productId?: string | number;
      productSizeId?: string | number;
      name: string;
      type: string;
      quantity: number;
      price: number | string;
      size?: string;
      color?: string;
      variety?: string;
      sku_code?: string;
      image_url?: string;
      isPack?: boolean;
      products?: Array<Record<string, unknown>>;
    }>;
    totalAmount: number;
    currency?: string;
    userInfo?: Record<string, unknown>;
    [key: string]: any;
  };
}
export async function createTempData(payload: GeneratePaymentLinkPayload): Promise<any> {
  try {
    // Only top-level / auth-session ids — nested userInfo may be ticket-scan only.
    const paymentUserId = resolvePaymentUserId(
      (payload.data?.userId as string | undefined) ??
        (payload.data?.user_id as string | undefined) ??
        null
    );
    const requestPayload: GeneratePaymentLinkPayload = paymentUserId
      ? {
          ...payload,
          data: {
            ...payload.data,
            userId: paymentUserId,
            user_id: paymentUserId,
          },
        }
      : payload;

    const response = await fetchWithRetry(`${baseUrl}/user/payment/temp-data`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestPayload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: errorText || `HTTP error! status: ${response.status}` };
      }

      return {
        success: false,
        error: errorData.message || errorData.error || `HTTP error! status: ${response.status}`,
      };
    }

    return {
      success: true,
      data: await response.json()
    };
  } catch (error: any) {
    console.error('Payment QR generation error:', error);
    return {
      success: false,
      error: error.message || 'Network error while generating payment QR',
    };
  }
}

export interface GeneratePaymentLinkRequest {
  url: string;
  type?: "onsite" | "offsite";
  /** Top-level user id — API appends it to the QR link when present. */
  data?: {
    userId?: string;
    user_id?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export async function scanQRPaymentAPI(
  id: string,
  payload: GeneratePaymentLinkRequest
): Promise<PaymentQRResponse> {
  try {
    const paymentUserId = resolvePaymentUserId(
      payload.data?.userId ?? payload.data?.user_id ?? null
    );
    const requestBody: GeneratePaymentLinkRequest = {
      ...payload,
      ...(paymentUserId
        ? {
            data: {
              ...(payload.data || {}),
              userId: paymentUserId,
              user_id: paymentUserId,
            },
          }
        : {}),
    };

    const response = await fetchWithRetry(`${baseUrl}/user/payment/generate-payment-link/${id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: errorText || `HTTP error! status: ${response.status}` };
      }

      return {
        success: false,
        error: errorData.message || errorData.error || `HTTP error! status: ${response.status}`,
      };
    }

    const contentType = response.headers.get('content-type');

    if (contentType && contentType.includes('application/json')) {
      const responseData = await response.json();

      if (typeof responseData === 'string') {
        return {
          success: true,
          data: responseData,
        };
      } else if (responseData.data) {
        return {
          success: true,
          data: responseData.data,
          message: responseData.message,
        };
      } else {
        return {
          success: true,
          data: responseData,
          message: responseData.message,
        };
      }
    } else {
      const responseText = await response.text();

      if (responseText.startsWith('http://') || responseText.startsWith('https://')) {
        return {
          success: true,
          data: responseText.trim(),
        };
      } else {
        try {
          const parsed = JSON.parse(responseText);
          return {
            success: true,
            data: parsed.data || parsed.qrCodeUrl || parsed.qrCode || parsed.paymentLink || parsed,
            message: parsed.message,
          };
        } catch {
          return {
            success: true,
            data: responseText,
          };
        }
      }
    }
  } catch (error: any) {
    console.error('Payment QR generation error:', error);
    return {
      success: false,
      error: error.message || 'Network error while generating payment QR',
    };
  }
}

